// Feature registry (foundation-owned, pre-wired — don't edit it to add your
// feature; fill in your own `src/features/<feature>/index.tsx` instead).
//
// Each feature's index.tsx default-exports a FeatureModule:
//   - nav:    sidebar entries, grouped into the collapsible sections below and sorted by `order`
//   - routes: react-router RouteObjects rendered inside the signed-in app layout.
//             Use absolute paths that start with your nav path ("/crm", "/crm/:contactId").
// See apps/portal/README.md → "How to build a feature page".

import { ChartLine, LayoutGrid, MessageCircle, Target, TrendingUp, type LucideIcon } from "lucide-react";
import type { RouteObject } from "react-router";
import analytics from "./analytics";
import autoresponder from "./autoresponder";
import comments from "./comments";
import crm from "./crm";
import engage from "./engage";
import outreach from "./outreach";
import scheduler from "./scheduler";
import replies from "./replies";
import followers from "./followers";
import buddies from "./buddies";
import spotlight from "./spotlight";
import dream100 from "./dream100";
import autocaption from "./autocaption";
import autotag from "./autotag";
import snapshots from "./snapshots";

/**
 * Sidebar sections, in funnel order: reach new people (growth), post (content),
 * answer them (replies), work the leads (pipeline, shown as "Leads"), measure (insights).
 */
export type NavGroup = "growth" | "content" | "replies" | "pipeline" | "insights";

export interface FeatureNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  group: NavGroup;
  /** Lower comes first within the group. */
  order: number;
  /** Only active on this exact path (e.g. "/crm" shouldn't light up on "/crm/contacts"). */
  end?: boolean;
}

export interface FeatureModule {
  id: string;
  nav: FeatureNavItem[];
  routes: RouteObject[];
}

export const features: FeatureModule[] = [crm, outreach, engage, autoresponder, comments, scheduler, analytics, replies, followers, buddies, spotlight, dream100, autocaption, autotag, snapshots];

export const NAV_GROUP_LABELS: Record<NavGroup, string> = {
  growth: "Growth",
  content: "Content",
  replies: "Replies",
  pipeline: "Leads",
  insights: "Insights",
};

export const NAV_GROUP_ICONS: Record<NavGroup, LucideIcon> = {
  growth: TrendingUp,
  content: LayoutGrid,
  replies: MessageCircle,
  pipeline: Target,
  insights: ChartLine,
};

export const NAV_GROUP_ORDER: NavGroup[] = ["growth", "content", "replies", "pipeline", "insights"];

/** All feature nav items, grouped and sorted. */
export function featureNav(): Array<{ group: NavGroup; label: string; icon: LucideIcon; items: FeatureNavItem[] }> {
  const all = features.flatMap((f) => f.nav);
  return NAV_GROUP_ORDER.map((group) => ({
    group,
    label: NAV_GROUP_LABELS[group],
    icon: NAV_GROUP_ICONS[group],
    items: all.filter((n) => n.group === group).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label)),
  })).filter((g) => g.items.length > 0);
}

/** All feature routes, flattened, for the router. */
export function featureRoutes(): RouteObject[] {
  return features.flatMap((f) => f.routes);
}
